import * as designTokens from '@calm/evexia-design-tokens';
import { PaletteOptions } from '../index';

export const FACEBOOK_BRAND_COLOR = '#3B5998';

declare module '@mui/material/styles' {
    interface Palette {
        // It should have the same shape as primary
        monochrome: Palette['primary'];
        outlineBorder: string;
        inputLine: string;
        backdropOverlay: string;
        shadowOverlay: string;
        gradient: {
            backgroundPrimary: string;
            backgroundSecondary: string;
            iconPrimary: string;
            iconSecondary: string;
            calm: string;
            calmBrand: string;
            calmBrandAccessible: string;
        };
        mask: {
            black: {
                '4p': string;
                '12p': string;
                '20p': string;
                '40p': string;
                '60p': string;
                '80p': string;
            };
            white: {
                '8p': string;
                '12p': string;
                '20p': string;
                '30p': string;
                '40p': string;
                '60p': string;
                '80p': string;
            };
        };
    }

    interface PaletteOptions {
        // It should have the same shape as primary
        monochrome: PaletteOptions['primary'];
        outlineBorder: string;
        inputLine: string;
        backdropOverlay: string;
        shadowOverlay: string;
        gradient: {
            backgroundPrimary: string;
            backgroundSecondary: string;
            iconPrimary: string;
            iconSecondary: string;
            calm: string;
            calmBrand: string;
            calmBrandAccessible: string;
        };
        mask: {
            black: {
                '4p': string;
                '12p': string;
                '20p': string;
                '40p': string;
                '60p': string;
                '80p': string;
            };
            white: {
                '8p': string;
                '12p': string;
                '20p': string;
                '30p': string;
                '40p': string;
                '60p': string;
                '80p': string;
            };
        };
    }

    interface TypeBackground {
        snackbar: string;
    }

    interface PaletteColor {
        '4p': string;
        '8p': string;
        '12p': string;
        '16p': string;
        '30p': string;
        '30pRipple': string;
        '50p': string;
        '160p': string;
        '190p': string;
    }

    interface SimplePaletteColorOptions {
        '4p'?: string;
        '8p'?: string;
        '12p'?: string;
        '16p'?: string;
        '30p'?: string;
        '30pRipple'?: string;
        '50p'?: string;
        '160p'?: string;
        '190p'?: string;
    }

    interface TypeText {
        sub: string;
        body: string;
    }

    interface TypeBackground {
        primary: string;
        primaryLight: string;
        secondary: string;
        secondaryLight: string;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        monochrome: true;
    }
}

declare module '@mui/material/IconButton' {
    interface IconButtonPropsColorOverrides {
        monochrome: true;
    }
}

const mask = {
    black: {
        '4p': designTokens.DS_COLOR.BLACK_4P,
        '12p': designTokens.DS_COLOR.BLACK_12P,
        '20p': designTokens.DS_COLOR.BLACK_20P,
        '40p': designTokens.DS_COLOR.BLACK_40P,
        '60p': designTokens.DS_COLOR.BLACK_60P,
        '80p': designTokens.DS_COLOR.BLACK_80P,
    },
    white: {
        '8p': designTokens.DS_COLOR.WHITE_8P,
        '12p': designTokens.DS_COLOR.WHITE_12P,
        '20p': designTokens.DS_COLOR.WHITE_20P,
        '30p': designTokens.DS_COLOR.WHITE_30P,
        '40p': designTokens.DS_COLOR.WHITE_40P,
        '60p': designTokens.DS_COLOR.WHITE_60P,
        '80p': designTokens.DS_COLOR.WHITE_80P,
    },
};

export const lightPalette: PaletteOptions = {
    mode: 'light',
    monochrome: {
        light: designTokens.DS_COLOR.WHITE_100P,
        main: designTokens.DS_COLOR.WHITE_100P,
        dark: designTokens.DS_COLOR.GREY,
        contrastText: designTokens.DS_COLOR.LIGHT_TEXT_BODY,
    },
    text: {
        primary: designTokens.DS_COLOR.LIGHT_TEXT_PRIMARY_HEADER,
        secondary: designTokens.DS_COLOR.LIGHT_TEXT_SECONDARY_HEADER,
        disabled: designTokens.DS_COLOR.LIGHT_TEXT_DISABLED,
        sub: designTokens.DS_COLOR.LIGHT_TEXT_SUB,
        body: designTokens.DS_COLOR.LIGHT_TEXT_BODY,
    },
    primary: {
        main: designTokens.DS_COLOR.LIGHT_PRIMARY_MAIN,
        dark: designTokens.DS_COLOR.LIGHT_PRIMARY_DARK,
        light: designTokens.DS_COLOR.LIGHT_PRIMARY_LIGHT,
        contrastText: designTokens.DS_COLOR.LIGHT_PRIMARY_TEXT_CONTRAST,
        '4p': designTokens.DS_COLOR.LIGHT_PRIMARY_4P,
        '8p': designTokens.DS_COLOR.LIGHT_PRIMARY_8P,
        '12p': designTokens.DS_COLOR.LIGHT_PRIMARY_12P,
        '30p': designTokens.DS_COLOR.LIGHT_PRIMARY_30P,
        '30pRipple': designTokens.DS_COLOR.LIGHT_PRIMARY_30P,
        '50p': designTokens.DS_COLOR.LIGHT_PRIMARY_50P,
    },
    secondary: {
        main: designTokens.DS_COLOR.LIGHT_SECONDARY_MAIN,
        dark: designTokens.DS_COLOR.LIGHT_SECONDARY_DARK,
        light: designTokens.DS_COLOR.LIGHT_SECONDARY_LIGHT,
        contrastText: designTokens.DS_COLOR.LIGHT_SECONDARY_TEXT_CONTRAST,
        '4p': designTokens.DS_COLOR.LIGHT_SECONDARY_4P,
        '8p': designTokens.DS_COLOR.LIGHT_SECONDARY_8P,
        '12p': designTokens.DS_COLOR.LIGHT_SECONDARY_12P,
        '30p': designTokens.DS_COLOR.LIGHT_SECONDARY_30P,
        '30pRipple': designTokens.DS_COLOR.LIGHT_SECONDARY_30P,
        '50p': designTokens.DS_COLOR.LIGHT_SECONDARY_50P,
    },
    error: {
        main: designTokens.DS_COLOR.LIGHT_ERROR_MAIN,
        dark: designTokens.DS_COLOR.LIGHT_ERROR_DARK,
        light: designTokens.DS_COLOR.LIGHT_ERROR_LIGHT,
        contrastText: designTokens.DS_COLOR.LIGHT_ERROR_TEXT_CONTRAST,
        '4p': designTokens.DS_COLOR.LIGHT_ERROR_4P,
        '8p': designTokens.DS_COLOR.LIGHT_ERROR_8P,
        '12p': designTokens.DS_COLOR.LIGHT_ERROR_12P,
        '30p': designTokens.DS_COLOR.LIGHT_ERROR_30P,
        '30pRipple': designTokens.DS_COLOR.LIGHT_ERROR_30P,
        '50p': designTokens.DS_COLOR.LIGHT_ERROR_50P,
        '160p': designTokens.DS_COLOR.LIGHT_ERROR_160P,
        '190p': designTokens.DS_COLOR.LIGHT_ERROR_190P,
    },
    warning: {
        main: designTokens.DS_COLOR.LIGHT_WARNING_MAIN,
        dark: designTokens.DS_COLOR.LIGHT_WARNING_DARK,
        light: designTokens.DS_COLOR.LIGHT_WARNING_LIGHT,
        contrastText: designTokens.DS_COLOR.LIGHT_WARNING_TEXT_CONTRAST,
        '4p': designTokens.DS_COLOR.LIGHT_WARNING_4P,
        '8p': designTokens.DS_COLOR.LIGHT_WARNING_8P,
        '12p': designTokens.DS_COLOR.LIGHT_WARNING_12P,
        '30p': designTokens.DS_COLOR.LIGHT_WARNING_30P,
        '30pRipple': designTokens.DS_COLOR.LIGHT_WARNING_30P,
        '50p': designTokens.DS_COLOR.LIGHT_WARNING_50P,
        '160p': designTokens.DS_COLOR.LIGHT_WARNING_160P,
        '190p': designTokens.DS_COLOR.LIGHT_WARNING_190P,
    },
    info: {
        main: designTokens.DS_COLOR.LIGHT_INFO_MAIN,
        dark: designTokens.DS_COLOR.LIGHT_INFO_DARK,
        light: designTokens.DS_COLOR.LIGHT_INFO_LIGHT,
        contrastText: designTokens.DS_COLOR.LIGHT_INFO_TEXT_CONTRAST,
        '4p': designTokens.DS_COLOR.LIGHT_INFO_4P,
        '8p': designTokens.DS_COLOR.LIGHT_INFO_8P,
        '12p': designTokens.DS_COLOR.LIGHT_INFO_12P,
        '30p': designTokens.DS_COLOR.LIGHT_INFO_30P,
        '30pRipple': designTokens.DS_COLOR.LIGHT_INFO_30P,
        '50p': designTokens.DS_COLOR.LIGHT_INFO_50P,
        '160p': designTokens.DS_COLOR.LIGHT_INFO_160P,
        '190p': designTokens.DS_COLOR.LIGHT_INFO_190P,
    },
    success: {
        main: designTokens.DS_COLOR.LIGHT_SUCCESS_MAIN,
        dark: designTokens.DS_COLOR.LIGHT_SUCCESS_DARK,
        light: designTokens.DS_COLOR.LIGHT_SUCCESS_LIGHT,
        contrastText: designTokens.DS_COLOR.LIGHT_SUCCESS_TEXT_CONTRAST,
        '4p': designTokens.DS_COLOR.LIGHT_SUCCESS_4P,
        '8p': designTokens.DS_COLOR.LIGHT_SUCCESS_8P,
        '12p': designTokens.DS_COLOR.LIGHT_SUCCESS_12P,
        '30p': designTokens.DS_COLOR.LIGHT_SUCCESS_30P,
        '30pRipple': designTokens.DS_COLOR.LIGHT_SUCCESS_30P,
        '50p': designTokens.DS_COLOR.LIGHT_SUCCESS_50P,
        '160p': designTokens.DS_COLOR.LIGHT_SUCCESS_160P,
        '190p': designTokens.DS_COLOR.LIGHT_SUCCESS_190P,
    },
    action: {
        active: designTokens.DS_COLOR.LIGHT_ACTION_ACTIVE,
        hover: designTokens.DS_COLOR.LIGHT_ACTION_HOVER,
        selected: designTokens.DS_COLOR.LIGHT_ACTION_SELECTED,
        disabled: designTokens.DS_COLOR.LIGHT_ACTION_DISABLED,
        disabledBackground:
            designTokens.DS_COLOR.LIGHT_ACTION_DISABLED_BACKGROUND,
        focus: designTokens.DS_COLOR.LIGHT_ACTION_FOCUS,
    },
    background: {
        paper: designTokens.DS_COLOR.LIGHT_BACKGROUND_PAPER,
        default: designTokens.DS_COLOR.LIGHT_BACKGROUND_DEFAULT,
        snackbar: designTokens.DS_COLOR.LIGHT_SNACKBAR_BACKGROUND,
        primary: designTokens.DS_COLOR.LIGHT_BACKGROUND_PRIMARY_START,
        primaryLight: designTokens.DS_COLOR.LIGHT_BACKGROUND_PRIMARY_END,
        secondary: designTokens.DS_COLOR.LIGHT_BACKGROUND_SECONDARY_START,
        secondaryLight: designTokens.DS_COLOR.LIGHT_BACKGROUND_SECONDARY_END,
    },
    divider: designTokens.DS_COLOR.LIGHT_DIVIDER,
    outlineBorder: designTokens.DS_COLOR.LIGHT_OUTLINE_BORDER,
    inputLine: designTokens.DS_COLOR.LIGHT_INPUT_LINE,
    backdropOverlay: designTokens.DS_COLOR.LIGHT_BACKDROP_OVERLAY,
    shadowOverlay: designTokens.DS_COLOR.WHITE_100P,
    gradient: {
        backgroundPrimary: designTokens.DS_GRADIENT.LIGHT_BACKGROUND_PRIMARY,
        backgroundSecondary:
            designTokens.DS_GRADIENT.LIGHT_BACKGROUND_SECONDARY,
        iconPrimary: designTokens.DS_GRADIENT.LIGHT_ICON_PRIMARY,
        iconSecondary: designTokens.DS_GRADIENT.LIGHT_ICON_SECONDARY,
        calm: designTokens.DS_GRADIENT.CALM_HEALTH,
        calmBrand: designTokens.DS_GRADIENT.CALM_BRAND,
        calmBrandAccessible: designTokens.DS_GRADIENT.CALM_BRAND_ACCESSIBLE,
    },
    mask,
};

export const darkPalette: PaletteOptions = {
    mode: 'dark',
    monochrome: {
        light: designTokens.DS_COLOR.BLACK_100P,
        main: designTokens.DS_COLOR.DARK_PRIMARY_TEXT_CONTRAST,
        dark: designTokens.DS_COLOR.GREY,
        contrastText: designTokens.DS_COLOR.DARK_PRIMARY_MAIN,
    },
    text: {
        primary: designTokens.DS_COLOR.DARK_TEXT_PRIMARY_HEADER,
        secondary: designTokens.DS_COLOR.DARK_TEXT_SECONDARY_HEADER,
        disabled: designTokens.DS_COLOR.DARK_TEXT_DISABLED,
        body: designTokens.DS_COLOR.DARK_TEXT_BODY,
        sub: designTokens.DS_COLOR.DARK_TEXT_SUB,
    },
    primary: {
        main: designTokens.DS_COLOR.DARK_PRIMARY_MAIN,
        dark: designTokens.DS_COLOR.DARK_PRIMARY_DARK,
        light: designTokens.DS_COLOR.DARK_PRIMARY_LIGHT,
        contrastText: designTokens.DS_COLOR.DARK_PRIMARY_TEXT_CONTRAST,
        '8p': designTokens.DS_COLOR.DARK_PRIMARY_8P,
        '12p': designTokens.DS_COLOR.DARK_PRIMARY_12P,
        '16p': designTokens.DS_COLOR.DARK_PRIMARY_16P,
        '30p': designTokens.DS_COLOR.DARK_PRIMARY_30P,
        '30pRipple': designTokens.DS_COLOR.DARK_PRIMARY_30P,
        '50p': designTokens.DS_COLOR.DARK_PRIMARY_50P,
    },
    secondary: {
        main: designTokens.DS_COLOR.DARK_SECONDARY_MAIN,
        dark: designTokens.DS_COLOR.DARK_SECONDARY_DARK,
        light: designTokens.DS_COLOR.DARK_SECONDARY_LIGHT,
        contrastText: designTokens.DS_COLOR.DARK_SECONDARY_TEXT_CONTRAST,
        '8p': designTokens.DS_COLOR.DARK_SECONDARY_8P,
        '12p': designTokens.DS_COLOR.DARK_SECONDARY_12P,
        '16p': designTokens.DS_COLOR.DARK_SECONDARY_16P,
        '30p': designTokens.DS_COLOR.DARK_SECONDARY_30P,
        '30pRipple': designTokens.DS_COLOR.DARK_SECONDARY_30P,
        '50p': designTokens.DS_COLOR.DARK_SECONDARY_50P,
    },
    error: {
        main: designTokens.DS_COLOR.DARK_ERROR_MAIN,
        dark: designTokens.DS_COLOR.DARK_ERROR_DARK,
        light: designTokens.DS_COLOR.DARK_ERROR_LIGHT,
        contrastText: designTokens.DS_COLOR.DARK_ERROR_TEXT_CONTRAST,
        '8p': designTokens.DS_COLOR.DARK_ERROR_8P,
        '12p': designTokens.DS_COLOR.DARK_ERROR_12P,
        '30p': designTokens.DS_COLOR.DARK_ERROR_30P,
        '30pRipple': designTokens.DS_COLOR.DARK_ERROR_30P,
        '50p': designTokens.DS_COLOR.DARK_ERROR_50P,
        '160p': designTokens.DS_COLOR.DARK_ERROR_160P,
        '190p': designTokens.DS_COLOR.DARK_ERROR_190P,
    },
    warning: {
        main: designTokens.DS_COLOR.DARK_WARNING_MAIN,
        dark: designTokens.DS_COLOR.DARK_WARNING_DARK,
        light: designTokens.DS_COLOR.DARK_WARNING_LIGHT,
        contrastText: designTokens.DS_COLOR.DARK_WARNING_TEXT_CONTRAST,
        '8p': designTokens.DS_COLOR.DARK_WARNING_8P,
        '12p': designTokens.DS_COLOR.DARK_WARNING_12P,
        '30p': designTokens.DS_COLOR.DARK_WARNING_30P,
        '30pRipple': designTokens.DS_COLOR.DARK_WARNING_30P,
        '50p': designTokens.DS_COLOR.DARK_WARNING_50P,
        '160p': designTokens.DS_COLOR.DARK_WARNING_160P,
        '190p': designTokens.DS_COLOR.DARK_WARNING_190P,
    },
    info: {
        main: designTokens.DS_COLOR.DARK_INFO_MAIN,
        dark: designTokens.DS_COLOR.DARK_INFO_DARK,
        light: designTokens.DS_COLOR.DARK_INFO_LIGHT,
        contrastText: designTokens.DS_COLOR.DARK_INFO_TEXT_CONTRAST,
        '8p': designTokens.DS_COLOR.DARK_INFO_8P,
        '12p': designTokens.DS_COLOR.DARK_INFO_12P,
        '30p': designTokens.DS_COLOR.DARK_INFO_30P,
        '30pRipple': designTokens.DS_COLOR.DARK_INFO_30P,
        '50p': designTokens.DS_COLOR.DARK_INFO_50P,
        '160p': designTokens.DS_COLOR.DARK_INFO_160P,
        '190p': designTokens.DS_COLOR.DARK_INFO_190P,
    },
    success: {
        main: designTokens.DS_COLOR.DARK_SUCCESS_MAIN,
        dark: designTokens.DS_COLOR.DARK_SUCCESS_DARK,
        light: designTokens.DS_COLOR.DARK_SUCCESS_LIGHT,
        contrastText: designTokens.DS_COLOR.DARK_SUCCESS_TEXT_CONTRAST,
        '8p': designTokens.DS_COLOR.DARK_SUCCESS_8P,
        '12p': designTokens.DS_COLOR.DARK_SUCCESS_12P,
        '30p': designTokens.DS_COLOR.DARK_SUCCESS_30P,
        '30pRipple': designTokens.DS_COLOR.DARK_SUCCESS_30P,
        '50p': designTokens.DS_COLOR.DARK_SUCCESS_50P,
        '160p': designTokens.DS_COLOR.DARK_SUCCESS_160P,
        '190p': designTokens.DS_COLOR.DARK_SUCCESS_190P,
    },
    action: {
        active: designTokens.DS_COLOR.DARK_ACTION_ACTIVE,
        hover: designTokens.DS_COLOR.DARK_ACTION_HOVER,
        selected: designTokens.DS_COLOR.DARK_ACTION_SELECTED,
        disabled: designTokens.DS_COLOR.DARK_ACTION_DISABLED,
        disabledBackground:
            designTokens.DS_COLOR.DARK_ACTION_DISABLED_BACKGROUND,
        focus: designTokens.DS_COLOR.DARK_ACTION_FOCUS,
    },
    background: {
        paper: designTokens.DS_COLOR.DARK_BACKGROUND_PAPER,
        default: designTokens.DS_COLOR.DARK_BACKGROUND_DEFAULT,
        snackbar: designTokens.DS_COLOR.DARK_SNACKBAR_BACKGROUND,
        primary: designTokens.DS_COLOR.DARK_BACKGROUND_PRIMARY_START,
        primaryLight: designTokens.DS_COLOR.DARK_BACKGROUND_PRIMARY_END,
        secondary: designTokens.DS_COLOR.DARK_BACKGROUND_SECONDARY_START,
        secondaryLight: designTokens.DS_COLOR.DARK_BACKGROUND_SECONDARY_END,
    },
    gradient: {
        backgroundPrimary: designTokens.DS_GRADIENT.DARK_BACKGROUND_PRIMARY,
        backgroundSecondary: designTokens.DS_GRADIENT.DARK_BACKGROUND_SECONDARY,
        iconPrimary: designTokens.DS_GRADIENT.DARK_ICON_PRIMARY,
        iconSecondary: designTokens.DS_GRADIENT.DARK_ICON_SECONDARY,
        calm: designTokens.DS_GRADIENT.CALM_HEALTH,
        calmBrand: designTokens.DS_GRADIENT.CALM_BRAND,
        calmBrandAccessible: designTokens.DS_GRADIENT.CALM_BRAND_ACCESSIBLE,
    },
    divider: designTokens.DS_COLOR.DARK_DIVIDER,
    outlineBorder: designTokens.DS_COLOR.DARK_OUTLINE_BORDER,
    inputLine: designTokens.DS_COLOR.DARK_INPUT_LINE,
    backdropOverlay: designTokens.DS_COLOR.DARK_BACKDROP_OVERLAY,
    shadowOverlay: designTokens.DS_COLOR.BLACK_100P,
    mask,
};
